/**
 * Compact table showing % of MAU using each platform feature. Horizontal
 * bar indicates share so operators can scan at a glance.
 */
import React from 'react';
import { CEDAR } from '../theme';
import { formatInt, formatPercent } from '../format';

export interface FeatureRow {
  feature: string;
  users: number;
  pct_of_mau: number;
}

export interface FeatureAdoptionTableProps {
  features: FeatureRow[];
  mau: number;
}

const LABELS: Record<string, string> = {
  chat: 'Chat messages',
  cta_click: '"Chat with Cedar" CTAs',
  debrief: 'Debrief completion',
  weekly_checkin: 'Weekly check-in',
  file_upload: 'File uploads',
  health_sync: 'Health data sync',
};

export const FeatureAdoptionTable: React.FC<FeatureAdoptionTableProps> = ({ features, mau }) => {
  if (!features.length || mau === 0) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-gray-500">
        No MAU yet — the table will populate once users are active.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-[11px] text-gray-500">
        {formatInt(mau)} monthly active users
      </div>
      <ul className="space-y-2.5">
        {features.map((f) => (
          <li key={f.feature}>
            <div className="flex items-baseline justify-between text-[12px]">
              <span className="font-semibold text-gray-800">
                {LABELS[f.feature] ?? f.feature}
              </span>
              <span className="tabular-nums text-gray-600">
                {formatInt(f.users)} · <b className="text-gray-900">{formatPercent(f.pct_of_mau)}</b>
              </span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.min(100, f.pct_of_mau)}%`,
                  background: `linear-gradient(90deg, ${CEDAR.stream} 0%, ${CEDAR.prime} 100%)`,
                }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
